/**
 * Utility functions for formatting numbers, particularly for currency exchange rates.
 * This module provides functions to format numbers with specific decimal places,
 * add thousands separators, and handle locale-specific number formats.
 */

// List of currency pairs that require higher precision (6 decimal places) when formatting exchange rates.
export const LONG_RATE_CURRENCY_PAIRS = ['HUF_PLN', 'JPY_PLN']

export const isLongRateCurrencyPair = (pair: string): boolean => LONG_RATE_CURRENCY_PAIRS.includes(pair)

/**
 * Formats a number or numeric string to a string with a specified number of fraction digits,
 * using a comma as the decimal separator.
 *
 * If the input is not a valid number, it defaults to `0`.
 * Accepts both numbers and strings (with either comma or dot as decimal separator).
 *
 * @param num - The number or numeric string to format.
 * @param fractionDigits - The number of digits to appear after the decimal separator.
 * @returns The formatted number as a string, with a comma as the decimal separator.
 */
export const formatNumber = (num: number | string, fractionDigits: number): string => {
  const numString = num.toString().replace(',', '.')
  const numberValue = parseFloat(numString)
  let valueToFormat = numberValue

  if (isNaN(valueToFormat)) {
    valueToFormat = 0
  }

  const fixedString = valueToFormat.toFixed(fractionDigits)

  return fixedString.replace('.', ',')
}

/**
 * Formats a number with a non-breaking space as the thousands separator and a comma as the decimal separator.
 * The function accepts a number or string, formats it to a specified number of decimal places (default: 2),
 * and inserts non-breaking spaces between groups of three digits in the integer part.
 * Uses toLocaleString with Polish locale for standard formatting.
 *
 * @param num - The number or string to format.
 * @param fractionDigits - The number of digits to appear after the decimal separator (default: 2).
 * @returns The formatted number as a string, with non-breaking spaces as thousands separators and a comma as the decimal separator.
 */
export const formatNumberWithSep = (num: number | string, fractionDigits: number = 2): string => {
  const formatted = formatNumber(num, fractionDigits)
  const parts = formatted.split(',')
  const intPart = parts[0]
  const decimalPart = parts[1]

  const intPartFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0')

  if (decimalPart !== undefined) {
    return `${intPartFormatted},${decimalPart}`
  }
  return intPartFormatted
}

/**
 * Formats a number by trimming unnecessary trailing zeros,
 * converting decimal points to commas, and adding non-breaking spaces
 * as thousands separators.
 *
 * - Accepts both numbers and strings as input.
 * - Throws an error if the input cannot be parsed as a valid number.
 * - Uses a comma as the decimal separator and non-breaking spaces for thousands.
 *
 * @param num - The number or string to format.
 * @returns The formatted number as a string.
 * @throws {Error} If the input is not a valid number.
 */
export const formatNumberTrimZeros = (num: number | string): string => {
  const normalizedString = num.toString().replace(',', '.')
  const parsed = parseFloat(normalizedString)

  if (isNaN(parsed)) {
    throw new Error('Invalid number format')
  }

  const trimmedString = parsed.toString()
  const parts = trimmedString.split('.')
  const intPart = parts[0]
  const decimalPart = parts[1]

  const intPartFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0')

  if (decimalPart !== undefined) {
    return `${intPartFormatted},${decimalPart}`
  }
  return intPartFormatted
}

/**
 * Determines the precision (number of decimal places) for displaying currency exchange rates.
 *
 * @param pair - The currency pair identifier in the format 'XXX_YYY'
 * @returns The number of decimal places to use for the rate:
 *          - 6 for pairs involving HUF or JPY
 *          - 4 for all other currency pairs
 */
export const getRatePrecision = (pair: string): number => {
  return LONG_RATE_CURRENCY_PAIRS.includes(pair) ? 6 : 4
}

/**
 * Validates currency pair format.
 *
 * @param pair - The currency pair to validate
 * @returns true if the format is valid (XXX_YYY where X is uppercase letter)
 */
const isValidCurrencyPairFormat = (pair: string): boolean => {
  return /^[A-Z]{3}_[A-Z]{3}$/.test(pair)
}

/**
 * Formats a currency exchange rate based on the provided currency pair.
 *
 * - Validates that the currency pair is a string in XXX_YYY format.
 * - Determines the number of fraction digits to use based on specific currency pairs.
 * - Uses 6 fraction digits for pairs in `longRatePairs`, otherwise uses 4.
 * - Returns the formatted rate as a string.
 *
 * @param rate - The exchange rate to format, as a number or string.
 * @param pair - The currency pair in the format 'XXX_YYY'.
 * @returns The formatted exchange rate as a string.
 * @throws {Error} If the currency pair format is invalid.
 */
export const formatRate = (rate: number | string, pair: string): string => {
  if (!isValidCurrencyPairFormat(pair)) {
    throw new Error('Invalid currency pair format')
  }

  const fractionDigits = getRatePrecision(pair)
  return formatNumber(rate, fractionDigits)
}

/**
 * Converts a locale-specific string representation of a number to a float-compatible string.
 * This function trims any whitespace from the input string and replaces commas with periods.
 *
 * @param value - The locale-specific string representation of a number.
 * @returns The float-compatible string representation of the number.
 */
export const localeStringToFloat = (value: string): string =>
  value
    .trim()
    .replace(/[^0-9,.-]/g, '') // Remove any non-numeric, non-comma, non-dot, non-dash characters
    .replace(/,/g, '.') // Replace commas with periods for decimal representation
